import React, { useEffect, useState, useRef } from 'react';
import Cookies from 'js-cookie';
import EmojiPicker from 'emoji-picker-react';
import { useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Image } from 'primereact/image';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';

import { socket } from 'lib/socket';
import { timestampToDatetime } from 'lib/functions';

import ChatTopbar from 'components/ChatTopbar';
import OnlineChatterList from 'components/OnlineChatterList';
import ChatMessageBubble from 'components/ChatMessageBubble';
import SystemMessageBubble from 'components/SystemMessageBubble';
import ChatSideBar from 'components/ChatSideBar';
import QuotedMessagePreview from 'components/QuotedMessagePreview';

const Chat = (props) => {
  const chatMessageBoardRef = useRef(null);
  const messageInputRef = useRef(null);
  const navigate = useNavigate();
  const [lastHeartBeat, setLastHeartBeat] = useState(Math.floor(Date.now() / 1000));
  const [chatter, setChatter] = useState(null);
  const [onlineChatters, setOnlineChatters] = useState([]);
  const [mutedChatterIds, setMutedChatterIds] = useState([]);
  const [selectedChatterId, setSelectedChatterId] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSayable, setIsSayable] = useState(true);
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [quotedMessage, setQuotedMessage] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVisible, setAlertVisible] = useState(false);
  const [sayPlaceholder, setSayPlaceholder] = useState('載入中，請稍候⋯');
  const [chatSidebarVisible, setChatSidebarVisible] = useState(false);
  const [isJustLoggedOut, setIsJustLoggedOut] = useState(false);
  const [chatterSettings, setChatterSettings] = useState({
    classic_message_layout: false
  });

  useEffect(() => {
    props.setPageParams({
      alias: 'chat',
      title: '聊天大廳'
    });
    setChatMessages([{
      id: '',
      sent_at: '',
      type: 'SYSTEM',
      room_id: null,
      chatter_id_from: null,
      chatter_id_to: null,
      message: '歡迎光臨三色貓聊天室！'
    }]);
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      const current = Math.floor(Date.now() / 1000);
      if ((current - lastHeartBeat) > process.env.REACT_APP_REQUEST_HEARTBEAT_SECOND) {
        requestHeartbeatResponse();
      }
      if (chatter !== null) {
        // request updating messages
        socket.emit('get-message', {
          chatter_id: chatter.chatter_id
        });
        // request updating chatters
        socket.emit('get-chatters', {
          chatter_id: chatter.chatter_id
        });
      }

    });
  }, [chatter]);

  // auto scroll to the bottom
  useEffect(() => {
    const container = chatMessageBoardRef.current;
    if (!container) return;
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= 10;
    if (isAtBottom) {
      setTimeout(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'instant' });
      }, 100);
    }
  }, [chatMessages]);

  useEffect(() => {
    var tmpSayPlaceholder = '對所有人說⋯';
    if (selectedChatterId !== null) {
      const selectedChatter = onlineChatters.filter(chatter => chatter.chatter_id === selectedChatterId);
      if (selectedChatter.length > 0) {
        tmpSayPlaceholder = `只對 ${selectedChatter[0].nickname} 說⋯`;
      }
    }
    setSayPlaceholder(tmpSayPlaceholder);
  }, [selectedChatterId]);

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }
  }, []);

  useEffect(() => {
    if (Cookies.get('chatter') === undefined) {
      navigate('/');
      return (() => {});
    }
    setChatter(JSON.parse(Cookies.get('chatter')));
    setIsLoaded(true);
  }, []);

  // listening to server...
  useEffect(() => {
    requestHeartbeatResponse();
    socket.on('updateLatestChattersResponse', updateLatestChattersResponse); // called from server to update latest chatter list
    socket.on('requestHeartbeatResponse', requestHeartbeatResponse); // called from server to send heartbeat
    socket.on('newMessagesGot', newMessagesGot); // called from server of new messages notification
    socket.on('updateMessagesResponse', updateMessagesResponse); // called from server to get new messages
    socket.on('requestLogoutResponse', requestLogoutResponse); // called from server to logout
    return () => {
      socket.off('updateLatestChattersResponse', updateLatestChattersResponse);
      socket.off('requestHeartbeatResponse', requestHeartbeatResponse);
      socket.off('updateMessagesResponse', updateMessagesResponse);
      socket.off('newMessagesGot', newMessagesGot);
      socket.off('requestLogoutResponse', requestLogoutResponse);
    };
  }, [chatter]);

  // send heartbeat
  const requestHeartbeatResponse = () => {
    // console.log('SEND HEART BEAT ', new Date())
    if (Cookies.get('chatter') === undefined) {
      navigate('/');
      return (() => {});
    }
    const chatter = JSON.parse(Cookies.get('chatter'));
    socket.emit('heartbeat', {
      chatter_id: chatter.chatter_id
    });
    setLastHeartBeat(Math.floor(Date.now() / 1000));
  };

  const updateLatestChattersResponse = (m) => {
    setOnlineChatters((prev) => {
      // find all offline chatters
      var offlineChatters = prev.filter(onlineChatter => m.filter(serverOnlineChatter => serverOnlineChatter.chatter_id === onlineChatter.chatter_id).length === 0);
      // merge offline chatters into end of server online chatters
      var updatedOnlineChatters = [
        ...m,
        ...offlineChatters.map((offlineChatter) => { return {...offlineChatter, offline: true}; })
      ];
      return updatedOnlineChatters;
    });
  };

  const newMessagesGot = (m) => {
    if (chatter !== null) {
      socket.emit('get-message', {
        chatter_id: chatter.chatter_id
      });
    }
  };

  const updateMessagesResponse = (m) => {
    // const {
    //   id = null,
    //   sent_at = null,
    //   type = null,
    //   room_id = null,
    //   chatter_id_from = null,
    //   chatter_id_to = null,
    //   quoted_id = null,
    //   message = null
    // } = m;
    // const newChatMessage = {
    //   id: id,
    //   sent_at: sent_at,
    //   type: type,
    //   room_id: room_id,
    //   chatter_id_from: chatter_id_from,
    //   chatter_id_to: chatter_id_to,
    //   quoted_id: quoted_id,
    //   message: message
    // };
    const {
      new_messages = []
    } = m;
    const newChatMessages = new_messages;
    setChatMessages((chatMessagesPrev) => {
      return [...chatMessagesPrev, ...newChatMessages];
    });
  };

  const requestLogoutResponse = () => {
    if (isJustLoggedOut) { return false; }
    setAlertMessage('你已因閒置太久而被登出，請重新進入聊天室。');
    setAlertVisible(true);
    navigate('/?timeout=1');
    return false;
  };

  const sendMessage = () => {
    if (!isSayable) {
      return false;
    }
    if (onlineChatters.length === 0) {
      setMessage('');
      return false;
    }
    if (message.trim() === '') {
      setMessage('');
      return false;
    }
    socket.emit('say', {
      chatter_id_from: chatter.chatter_id,
      chatter_id_to: selectedChatterId,
      quoted_id: quotedMessage===null ? null : quotedMessage.id,
      message: message
    });
    setMessage('');
    setQuotedMessage(null);
    setIsSayable(false);
    setTimeout(() => {
      setIsSayable(true);
    }, process.env.REACT_APP_SEND_INTERVAL_LIMIT_SECOND * 1000);
  };

  const clickMessage = (message) => {
    setQuotedMessage(message);
    messageInputRef.current?.focus();
  };

  const logout = () => {
    confirmDialog({
      message: '你確定要登出聊天室？',
      header: '登出',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        setIsJustLoggedOut(true);
        socket.emit('logout', {
          chatter_id: chatter.chatter_id,
        });
        navigate('/');
        return false;
      },
      reject: () => {}
    });
   };

  const getChatMessageObject = (chatMessage) => {
    if (chatMessage === null) {
      return null;
    }
    return {
      chatter: onlineChatters.filter(oc => oc.chatter_id === chatMessage.chatter_id_from)[0] ?? null,
      own: chatMessage.chatter_id_from === chatter.chatter_id,
      quoted_my_message: chatMessage.quoted_message?.chat_message?.chatter_id_from === chatter.chatter_id,
      chat_message: {
        ...chatMessage,
        message_datetime: timestampToDatetime(chatMessage.sent_at)
      }
    };
  };

  const selectChatter = (selectedChatterId) => {
    setSelectedChatterId(selectedChatterId);
    setChatSidebarVisible(false);
  };

  const checkIsChatterMuted = (chatterId) => {
    return mutedChatterIds.includes(chatterId);
  };

  const toggleMuteChatter = (chatterId) => {
    setMutedChatterIds((mutedChatterIds) => {
      var tmpMutedChatterIds = [];
      if (mutedChatterIds.includes(chatterId)) {
        tmpMutedChatterIds = mutedChatterIds.filter(mutedChatterId => mutedChatterId !== chatterId);
      } else {
        tmpMutedChatterIds = [...mutedChatterIds, chatterId];
      }
      return tmpMutedChatterIds;
    });
    // setTimeout(() => {
    //   setIsLoaded(isLoaded => isLoaded);
    // }, 1);
  };

  const TargetChatter = (props) => {
    if (props.selectedChatterId === null) {
      return <div className="flex flex-row align-items-center gap-2">
        {/* <Image src={`/favicon.png`} alt="Image" width="24" imageClassName="block" /> */}
        <div className=""><strong>聊天大廳</strong></div>
      </div>
    } else {
      // find online chatter
      const targetChatter = onlineChatters.filter(onlineChatter => onlineChatter.chatter_id === props.selectedChatterId);
      if (targetChatter.length === 0) {
        return <div className="flex flex-row align-items-center gap-2">
          {/* <Image src={`/favicon.png`} alt="Image" width="24" imageClassName="block" /> */}
          <div className=""><strong>[不明人士]</strong></div>
        </div>
      } else {
        return <div className="flex flex-row align-items-center gap-2">
          {/* <Image src={`/avatar/${targetChatter[0].avatar}.jpg`} alt="Image" width="24" imageClassName="block border-circle" /> */}
          <div className=""><strong>{targetChatter[0].nickname}</strong></div>
        </div>
      }
    }
  };

  const getChatterNickname = (chatterId) => {
    const targetChatter = onlineChatters.filter(onlineChatter => onlineChatter.chatter_id === chatterId);
    if (targetChatter.length > 0) {
      return targetChatter[0].nickname;
    } else {
      return '[不明人士]';
    }
  };

  const getChatMessage = (messageId) => {
    if (messageId === null) {
      return null;
    }
    const targetMessage = chatMessages.filter(chatMessage => chatMessage.id === messageId);
    if (targetMessage.length > 0) {
      return targetMessage[0];
    } else {
      return null;
    }
  };

  const updateChatterSettings = (updatedChatterSettings) => {
    setChatterSettings(updatedChatterSettings);
  };

  return chatter !== null ? (
    <>
      <div className="chat-container flex flex-column h-full gap-2 p-2 overflow-y-hidden">
        <div className="topbar"><ChatTopbar /></div>
        <div className="chat-panel flex flex-row flex-1 gap-2 h-1rem">
          <div className="chat-side w-3 hidden md:flex flex-column">
            <OnlineChatterList chatters={onlineChatters} me={chatter} selectedChatterId={selectedChatterId} mutedChatterIds={mutedChatterIds} onSelectChatter={selectChatter} chatterSettings={chatterSettings} onUpdateChatterSettings={updateChatterSettings} onLogout={logout} />
          </div>
          <div className="chat-main flex flex-column flex-1 gap-2">
            <div className="messages-board-topbar w-full border-round-top-lg bg-teal-100" style={{ marginBottom:'-0.5rem' }}>
              <div className="messages-board-topbar-inner p-3 flex flex-row align-items-center justify-content-between text-teal-900">
                <div className="target-chatter">
                  <TargetChatter selectedChatterId={selectedChatterId} />
                </div>
                <div className="target-chatter-tools">
                  {selectedChatterId !== null && <Button className="border-round-sm px-0 py-0" severity={checkIsChatterMuted(selectedChatterId)?'danger':'primary'} icon={`pi pi-volume-${checkIsChatterMuted(selectedChatterId)?'off':'up'}`} size="small" style={{ width: 24, height: 24 }} tooltip={checkIsChatterMuted(selectedChatterId)?'取消靜音':'靜音'} tooltipOptions={{ position: 'left' }} onClick={() => toggleMuteChatter(selectedChatterId)} />}
                </div>
              </div>
            </div>
            <div className="chat-message-board relative flex-1 flex flex-row p-2 border-round-bottom-lg h-full overflow-y-scroll chatroom-surface-100" ref={chatMessageBoardRef}>
              <div className="chat-message-board-inner w-full flex flex-column">
                {chatMessages.map((chatMessage, index) => {
                  if (chatMessage.type === 'SYSTEM') {
                    if (selectedChatterId === null || chatMessage.global) {
                      return <div key={index} className="message-row"><SystemMessageBubble bubbleContent={{ message: chatMessage.message }} chatterSettings={chatterSettings} /></div>;
                    }
                  }
                  if (chatMessage.type === 'CHAT') {
                    chatMessage.quoted_message = getChatMessageObject(getChatMessage(chatMessage.quoted_id));
                    if (checkIsChatterMuted(chatMessage.chatter_id_from)) {
                      return <i key={index}></i>;
                    }
                    if (selectedChatterId === null) {
                      if (chatMessage.chatter_id_to === null) {
                        return <div key={index} className="message-row"><ChatMessageBubble bubbleContent={getChatMessageObject(chatMessage)} chatterSettings={chatterSettings} onClick={() => {clickMessage(chatMessage)}} /></div>;
                      }
                    } else {
                      if (
                        (chatMessage.chatter_id_from === chatter.chatter_id && chatMessage.chatter_id_to === selectedChatterId) ||
                        (chatMessage.chatter_id_to === chatter.chatter_id && chatMessage.chatter_id_from === selectedChatterId)
                      ) {
                        return <div key={index} className="message-row"><ChatMessageBubble bubbleContent={getChatMessageObject(chatMessage)} chatterSettings={chatterSettings} onClick={() => {clickMessage(chatMessage)}} /></div>;
                      }
                    }
                  }
                })}
              </div>
            </div>
            <div className="chat-send-board-container flex flex-column border-round-lg chatroom-surface-100">
              {quotedMessage !== null && <QuotedMessagePreview message={quotedMessage.message} nickname={getChatterNickname(quotedMessage.chatter_id_from)} onCancel={() => {setQuotedMessage(null)}} />}
              <div className="chat-send-board-inner flex flex-1 flex-row p-2 border-round-lg gap-2 ">
                <div className="mobile-sidebar-toggle-button md:hidden">
                  <Button icon="pi pi-bars" size="small" className="w-3rem" onClick={() => setChatSidebarVisible(true)} />
                </div>
                <InputText ref={messageInputRef} id="send-message" value={message} className="flex-1 p-inputtext-sm" onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => {if(e.key==='Enter'){sendMessage()}}} maxLength="100" disabled={onlineChatters.length===0} placeholder={sayPlaceholder} />
                <Button label="" icon="pi pi-send" size="small" className="w-4rem" onClick={sendMessage} disabled={!isSayable || onlineChatters.length===0} />
                {/* <div className="emoji-container fixed hidden">
                  <EmojiPicker />
                </div> */}
              </div>
            </div>
          </div>
        </div>
        <ChatSideBar visible={chatSidebarVisible} setVisible={setChatSidebarVisible}>
          <OnlineChatterList chatters={onlineChatters} me={chatter} selectedChatterId={selectedChatterId} mutedChatterIds={mutedChatterIds} onSelectChatter={selectChatter} chatterSettings={chatterSettings} onUpdateChatterSettings={updateChatterSettings} onLogout={logout} />
        </ChatSideBar>
      </div>
      <Dialog header={'⚠️ 錯誤'} visible={alertVisible} style={{ width: 'calc(100% - 40px)', maxWidth: '400px' }} onHide={() => {if (!alertVisible) return; setAlertVisible(false); }}>
        <p className="m-0">{alertMessage}</p>
      </Dialog>
      <ConfirmDialog />
    </>
  ) : <></>;
};

export default Chat;
